import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import { v4 } from 'uuid';
import { useNetwork } from 'hooks';
import { API_URL } from 'constant';
import { ComplexSettingFormState } from 'views/pipeline/store/state';
import './field-binding-picker.scss';

/**
 * eSign field-binding picker (esign-in-builder / cross-pipeline references).
 *
 * Maps a template field (an eSign tab name) to a value produced by an EARLIER node — in this
 * pipeline or anywhere up the parent chain to the root. Sources come from
 * GET /complex-node-references/:nodeId?pipelineId=, which returns ONLY the guaranteed linear line to
 * the root (no sibling-branch data). The chosen binding is written to
 * complexSettingForm[nodeId].fieldBindings, which persists verbatim to actions[0].metadata.fieldBindings
 * (whole-object passthrough) and is read at signing time by the runtime resolver.
 */

interface IReferenceField {
	key: string;
	label: string;
	kind: string;
	source: string; // 'data' | 'provider'
}
interface IReferenceSource {
	pipelineId: string;
	pipelineName?: string;
	pipelineSlug?: string | null;
	distance: number;
	nodeId: string;
	slug: string | null;
	stepId: string;
	label: string;
	fields: IReferenceField[];
}
interface IBindingSource {
	pipelineId?: string; // omitted when the source is in the consumer's own pipeline (distance 0)
	nodeId: string;
	nodeSlug?: string | null;
	stepId: string;
	field: string;
}
interface IFieldBinding {
	id?: string; // client-only stable row key; runtime resolver ignores unknown keys
	templateField: string;
	label?: string; // display-only; the runtime resolver ignores unknown keys
	source: IBindingSource;
}

interface IProps {
	nodeId: string;
	pipelineId: string;
}

// A flat option in the source dropdown, one per (source node × field).
interface IFlatOption {
	value: string; // stable encode: pipelineId::nodeId::field
	label: string;
	src: IReferenceSource;
	field: IReferenceField;
}

const encode = (s: IReferenceSource, f: IReferenceField) =>
	`${s.pipelineId}::${s.nodeId}::${f.key}`;

export const FieldBindingPicker: FC<IProps> = ({ nodeId, pipelineId }) => {
	const { get } = useNetwork();
	const [complexSettingForm, setComplexSettingForm] = useRecoilState<any>(
		ComplexSettingFormState
	);
	const [sources, setSources] = useState<IReferenceSource[]>([]);
	const [loading, setLoading] = useState(false);

	const bindings: IFieldBinding[] = useMemo(
		() => complexSettingForm?.[nodeId]?.fieldBindings ?? [],
		[complexSettingForm, nodeId]
	);

	useEffect(() => {
		if (!nodeId || !pipelineId) return;
		setLoading(true);
		get(`${API_URL.COMPLEX_NODE_REFERENCES}/${nodeId}?pipelineId=${pipelineId}`)
			.then((resp: any) => {
				setSources(Array.isArray(resp?.sources) ? resp.sources : []);
			})
			.catch(() => setSources([]))
			.finally(() => setLoading(false));
	}, [get, nodeId, pipelineId]);

	// Flatten sources -> dropdown options, grouped nearest-first by pipeline then node.
	const options: IFlatOption[] = useMemo(() => {
		const out: IFlatOption[] = [];
		for (const s of sources) {
			const pipeLabel =
				s.distance === 0
					? 'This pipeline'
					: s.pipelineName || s.pipelineSlug || 'Parent pipeline';
			for (const f of s.fields) {
				out.push({
					value: encode(s, f),
					label: `${pipeLabel} › ${s.label} › ${f.label}`,
					src: s,
					field: f,
				});
			}
		}
		return out;
	}, [sources]);

	const writeBindings = useCallback(
		(next: IFieldBinding[]) => {
			setComplexSettingForm((prev: any) => {
				const newObj = structuredClone(prev ?? {});
				if (!newObj[nodeId]) newObj[nodeId] = {};
				newObj[nodeId].fieldBindings = next;
				return newObj;
			});
		},
		[nodeId, setComplexSettingForm]
	);

	const addRow = useCallback(() => {
		writeBindings([
			...bindings,
			{ id: v4(), templateField: '', source: { nodeId: '', stepId: '', field: '' } },
		]);
	}, [bindings, writeBindings]);

	const removeRow = useCallback(
		(idx: number) => {
			writeBindings(bindings.filter((_, i) => i !== idx));
		},
		[bindings, writeBindings]
	);

	const setTemplateField = useCallback(
		(idx: number, value: string) => {
			const next = bindings.map((b, i) =>
				i === idx ? { ...b, templateField: value } : b
			);
			writeBindings(next);
		},
		[bindings, writeBindings]
	);

	const setSource = useCallback(
		(idx: number, optionValue: string) => {
			const opt = options.find(o => o.value === optionValue);
			const next = bindings.map((b, i) => {
				if (i !== idx) return b;
				if (!opt) return b;
				const src: IBindingSource = {
					// Own-pipeline sources resolve against the current session; omit pipelineId so
					// the resolver does not look them up in the parent chain.
					...(opt.src.distance === 0
						? {}
						: { pipelineId: opt.src.pipelineId }),
					nodeId: opt.src.nodeId,
					nodeSlug: opt.src.slug,
					stepId: opt.src.stepId,
					field: opt.field.key,
				};
				return { ...b, source: src, label: opt.label };
			});
			writeBindings(next);
		},
		[bindings, options, writeBindings]
	);

	// Stable React key per row. New rows carry an `id`; rows persisted before `id` existed
	// fall back to a per-object id cached here (binding objects keep identity across renders,
	// so the key stays stable for the row's lifetime without polluting saved data).
	const legacyKeys = useRef(new WeakMap<IFieldBinding, string>());
	const keyFor = (b: IFieldBinding): string => {
		if (b.id) return b.id;
		let k = legacyKeys.current.get(b);
		if (!k) {
			k = v4();
			legacyKeys.current.set(b, k);
		}
		return k;
	};

	// The dropdown value for a row, reconstructed from its stored source.
	const selectedValueFor = (b: IFieldBinding): string => {
		const pid = b.source?.pipelineId ?? pipelineId; // own-pipeline sources stored without pid
		return `${pid}::${b.source?.nodeId}::${b.source?.field}`;
	};

	return (
		<div className="FieldBindingPicker">
			<div className="FieldBindingPicker__head">
				<div className="FieldBindingPicker__title">Prefill from earlier steps</div>
				<div className="FieldBindingPicker__hint">
					Map an eSign template field to a value collected by an earlier node in this
					pipeline or up the chain.
				</div>
			</div>

			{loading && <div className="FieldBindingPicker__empty">Loading available fields…</div>}

			{!loading && !options.length && (
				<div className="FieldBindingPicker__empty">
					No earlier nodes expose referenceable fields yet.
				</div>
			)}

			{!loading &&
				bindings.map((b, idx) => (
					<div className="FieldBindingPicker__row" key={keyFor(b)}>
						<input
							className="FieldBindingPicker__field"
							type="text"
							placeholder="eSign template field (tab name)"
							value={b.templateField}
							onChange={e => setTemplateField(idx, e.target.value)}
						/>
						<span className="FieldBindingPicker__arrow">←</span>
						<select
							className="FieldBindingPicker__source"
							value={selectedValueFor(b)}
							onChange={e => setSource(idx, e.target.value)}
						>
							<option value="">Select a source field…</option>
							{options.map(o => (
								<option key={o.value} value={o.value}>
									{o.label}
								</option>
							))}
						</select>
						<button
							className="FieldBindingPicker__remove"
							type="button"
							onClick={() => removeRow(idx)}
							aria-label="Remove mapping"
						>
							✕
						</button>
					</div>
				))}

			<button
				className="FieldBindingPicker__add"
				type="button"
				onClick={addRow}
				disabled={loading || !options.length}
			>
				+ Add mapping
			</button>
		</div>
	);
};
